import React, { useState, useEffect } from 'react';
import { Box } from '@chakra-ui/react';
import useData from '../../../data/useData';
import { Card, Button, Tag, Avatar, List, Modal, Divider } from 'antd';
import { DeleteOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import MediaDisplay from '../../../components/feed/MediaDisplay';
import { useMutation } from 'react-query';
import { mutateFunction } from '../../../libs/apis';
import { CustomToast } from '../../../components/toast/CustomToast';
import Header from '../../../components/header/Header';
import './post-reports.css';
import { useSelector } from 'react-redux';
import PermissionBlinder from '../../../components/PermissionBlinder';

const { confirm } = Modal;

const PostReport = () => {
  const { data, refetch, isLoading } = useData('admins/post-reports');
  const [postReports, setPostReports] = useState([]);
  const { addToast } = CustomToast();

  const permissions = useSelector(
    (state) => state?.user?.userInfo?.permissions,
  );
  const { mutate, isLoading: isPostDeleteLoading } =
    useMutation(mutateFunction);

  useEffect(() => {
    if (data) {
      setPostReports(data?.data?.postReports);
    }
  }, [data]);

  const showDeleteConfirm = (postId) => {
    confirm({
      title: 'Are you sure you want to delete this post?',
      icon: <ExclamationCircleOutlined />,
      content: 'This action cannot be undone.',
      okText: 'Yes, delete',
      okType: 'danger',
      cancelText: 'No',
      onOk() {
        mutate(
          {
            key: `admins/post-reports/${postId}`,
            method: 'delete',
          },
          {
            onSuccess(res) {
              addToast(res || { message: 'Post deleted successfully' });
              refetch();
            },
            onError(e) {
              addToast({
                message: 'Failed to delete post',
                type: 'error',
              });
            },
          },
        );
      },
    });
  };

  if(permissions && !permissions?.can_manage_post_reports){
    return <PermissionBlinder />
  }

  return (
    <Box>
      <Header title="Post Reports" />
      <div className="post-report-container">
        <h2 style={{ fontWeight: 700, marginBottom: 24 }}>Post Reports</h2>
        {isLoading ? (
          <div>Loading...</div>
        ) : (
          <>
            {postReports?.length === 0 ? (
              <div>No post reports found</div>
            ) : (
              <div
                className="hide-scroll"
                style={{ maxHeight: 'calc(100vh - 120px)', overflowY: 'auto' }}
              >
                <List
                  grid={{ gutter: 24, column: 1 }}
                  dataSource={postReports}
                  renderItem={({
                    id,
                    details,
                    createdAt,
                    post,
                    user,
                    reason,
                  }) => (
                    <List.Item>
                      <Card
                        className="post-report-card"
                        title={
                          <span
                            className="post-report-title"
                            style={{ fontWeight: 600, fontSize: 18 }}
                          >
                            <ExclamationCircleOutlined
                              style={{ color: '#faad14', marginRight: 8 }}
                            />
                            Report #{id}
                          </span>
                        }
                        extra={
                          <span
                            className="post-report-extra"
                            style={{ color: '#888', fontSize: 13 }}
                          >
                            Reported on:{' '}
                            {new Date(createdAt).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                          </span>
                        }
                        bodyStyle={{ padding: 24 }}
                        hoverable
                      >
                        <div style={{ marginBottom: 12 }}>
                          <strong>Reason:</strong>{' '}
                          <span style={{ color: '#d46b08' }}>{reason}</span>
                          <br />
                          <strong>Details:</strong>{' '}
                          <span style={{ color: '#595959' }}>{details}</span>
                        </div>
                        <Divider style={{ margin: '16px 0' }} />
                        <div
                          className="post-report-flex"
                          style={{ display: 'flex', gap: 32, flexWrap: 'wrap' }}
                        >
                          {/* Post Details */}
                          <div style={{ flex: 2, minWidth: 320 }}>
                            <h4 style={{ fontWeight: 600, marginBottom: 8 }}>
                              Post Details
                            </h4>
                            <div
                              style={{
                                marginBottom: 8,
                                wordBreak: 'break-word',
                              }}
                            >
                              <strong>Text:</strong>{' '}
                              <span style={{ color: '#262626' }}>
                                {post.text}
                              </span>
                            </div>
                            {post.media && post.media.length > 0 && (
                              <div
                                style={{
                                  margin: '16px 0',
                                  background: '#f5f5f5',
                                  borderRadius: 8,
                                  padding: 12,
                                  textAlign: 'center',
                                  maxWidth: 500,
                                }}
                              >
                                <MediaDisplay
                                  media={post.media}
                                  style={{
                                    maxHeight: 320,
                                    objectFit: 'contain',
                                    margin: '0 auto',
                                  }}
                                />
                              </div>
                            )}
                            <div
                              className="post-report-stats"
                              style={{ margin: '10px 0' }}
                            >
                              <Tag color="blue">
                                Comments: {post.comment_count}
                              </Tag>
                              <Tag color="green">Likes: {post.likes_count}</Tag>
                              <Tag color="purple">
                                Shares: {post.share_count}
                              </Tag>
                            </div>
                            <div
                              className="post-report-tags"
                              style={{ marginBottom: 12 }}
                            >
                              {post.tags &&
                                post.tags.map((tag, idx) => (
                                  <Tag key={idx} color="magenta">
                                    {tag.text}
                                  </Tag>
                                ))}
                            </div>
                            <div style={{ textAlign: 'right', marginTop: 16 }}>
                              <Button
                                type="primary"
                                danger
                                icon={<DeleteOutlined />}
                                onClick={() => showDeleteConfirm(id)}
                              >
                                {isPostDeleteLoading
                                  ? 'Deleting...'
                                  : 'Delete Post'}
                              </Button>
                            </div>
                          </div>
                          {/* Reporting User */}
                          <div
                            className="post-report-user"
                            style={{
                              flex: 1,
                              minWidth: 240,
                              background: '#fafafa',
                              borderRadius: 8,
                              padding: 16,
                              textAlign: 'center',
                              boxShadow: '0 1px 4px rgba(0,0,0,0.03)',
                              alignSelf: 'flex-start',
                            }}
                          >
                            <h4 style={{ fontWeight: 600, marginBottom: 12 }}>
                              Reported By
                            </h4>
                            <Avatar
                              size={48}
                              style={{
                                backgroundColor: '#87d068',
                                marginBottom: 8,
                              }}
                            >
                              {user.first_name[0]}
                            </Avatar>
                            <div style={{ fontWeight: 500, fontSize: 16 }}>
                              {user.first_name} {user.last_name}
                            </div>
                            <div
                              style={{
                                color: '#888',
                                fontSize: 13,
                                wordBreak: 'break-word',
                              }}
                            >
                              {user.email}
                            </div>
                          </div>
                        </div>
                      </Card>
                    </List.Item>
                  )}
                />
              </div>
            )}
          </>
        )}
      </div>
    </Box>
  );
};

export default PostReport;
